// components/settings/surfox-settings/sections/CompanyProfileSection.jsx
// Refactored Company Profile with enterprise polish
// ALL existing logic preserved, UI redesigned

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  User,
  Phone,
  Mail,
  Globe,
  MapPin,
  Briefcase,
  Clock,
  Calendar,
  Target,
  Info,
  Check,
  Loader2,
  AlertCircle,
  DollarSign,
  Lock
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../lib/permissions';
import supabase from '../../../../lib/supabaseClient';
import {
  isOutOfTcpaBounds,
  clampToTcpaHours,
  formatHour,
  TCPA_WINDOW_LABEL,
} from '../../../../lib/tcpaHours';

// ============================================================================
// CONSTANTS
// ============================================================================

const HOUR_OPTIONS = [
  { label: '8:00 AM', value: '8' },
  { label: '9:00 AM', value: '9' },
  { label: '10:00 AM', value: '10' },
  { label: '11:00 AM', value: '11' },
];

const CLOSING_HOUR_OPTIONS = [
  { label: '4:00 PM', value: '16' },
  { label: '5:00 PM', value: '17' },
  { label: '6:00 PM', value: '18' },
  { label: '7:00 PM', value: '19' },
  { label: '8:00 PM', value: '20' },
];

// Reply hours can run wider than initial outreach, but NOT outside TCPA quiet
// hours (8:00 AM – 9:00 PM). 6:00 AM / 7:00 AM / 10:00 PM used to be offered here
// and two live tenants selected 6:00 AM–10:00 PM, so the AI was legally texting
// leads before dawn until the server-side clamp shipped. Do not re-add options
// outside TCPA_EARLIEST_HOUR..TCPA_LATEST_HOUR — the server discards them anyway
// (clampToTcpaHours in _shared/timezone.ts), so all offering them achieves is
// showing the tenant a number that isn't what happens.
const REPLY_OPEN_HOUR_OPTIONS = [
  { label: '8:00 AM', value: '8' },
  { label: '9:00 AM', value: '9' },
  { label: '10:00 AM', value: '10' },
  { label: '11:00 AM', value: '11' },
];

const REPLY_CLOSING_HOUR_OPTIONS = [
  { label: '5:00 PM', value: '17' },
  { label: '6:00 PM', value: '18' },
  { label: '7:00 PM', value: '19' },
  { label: '8:00 PM', value: '20' },
  { label: '9:00 PM', value: '21' },
];

const DAY_OPTIONS = ['M–F', 'M–Sat', 'Everyday'];

const TIMEZONE_OPTIONS = [
  { label: 'Eastern (EST)', value: 'EST' },
  { label: 'Central (CST)', value: 'CST' },
  { label: 'Mountain (MST)', value: 'MST' },
  { label: 'Pacific (PST)', value: 'PST' },
];

const BUSINESS_TYPE_OPTIONS = [
  { label: 'LLC', value: 'llc' },
  { label: 'Corporation', value: 'corp' },
  { label: 'Sole Proprietor', value: 'sole' },
  { label: 'Nonprofit', value: 'nonprofit' },
  { label: 'Other', value: 'other' },
];

const AVG_DEAL_VALUE_MIN = 100;
const AVG_DEAL_VALUE_MAX = 5000000;

const validateAvgDealValue = (raw) => {
  if (raw === '' || raw === null || raw === undefined) {
    return 'Average deal value is required.';
  }
  const num = Number(raw);
  if (!Number.isFinite(num)) return 'Enter a valid number.';
  if (!Number.isInteger(num)) return 'Enter a whole dollar amount (no cents).';
  if (num < AVG_DEAL_VALUE_MIN) return `Must be at least $${AVG_DEAL_VALUE_MIN.toLocaleString()}.`;
  if (num > AVG_DEAL_VALUE_MAX) return `Must be $${AVG_DEAL_VALUE_MAX.toLocaleString()} or less.`;
  return '';
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }
  return 'https://api.getsurfox.com/api';
};

const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      throw new Error(`Authentication error: ${sessionError.message}`);
    }

    if (!session?.access_token) {
      throw new Error('No valid session found. Please log in again.');
    }

    const apiBaseUrl = getApiBaseUrl();
    const fullUrl = `${apiBaseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options.headers
    };

    const response = await fetch(fullUrl, {
      ...options,
      headers
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error(`API endpoint not found.`);
      }

      let errorMessage = `Request failed: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (parseError) {
        const responseText = await response.text();
        errorMessage = `${errorMessage}. Response: ${responseText.substring(0, 200)}...`;
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    
    if (!responseText) {
      return {};
    }

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Invalid JSON response from API`);
    }
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

// ============================================================================
// STYLED INPUT COMPONENTS
// ============================================================================

const FormInput = ({ icon: Icon, label, disabled, ...props }) => (
  <div>
    {label && (
      <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span>{label}</span>
      </label>
    )}
    <input
      {...props}
      disabled={disabled}
      className={`
        w-full px-4 py-2.5 border border-slate-200 rounded-xl text-slate-900 
        placeholder-slate-400 bg-white
        focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
        disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
        transition-all duration-200
      `}
    />
  </div>
);

const FormSelect = ({ icon: Icon, label, options, disabled, ...props }) => (
  <div>
    {label && (
      <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span>{label}</span>
      </label>
    )}
    <div className="relative">
      <select
        {...props}
        disabled={disabled}
        className={`
          w-full px-4 py-2.5 border border-slate-200 rounded-xl text-slate-900 
          bg-white appearance-none
          focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          transition-all duration-200
        `}
      >
        {options}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
);

const FormTextarea = ({ icon: Icon, label, disabled, ...props }) => (
  <div>
    {label && (
      <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span>{label}</span>
      </label>
    )}
    <textarea
      {...props}
      disabled={disabled}
      className={`
        w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-900 
        placeholder-slate-400 bg-white resize-none
        focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
        disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
        transition-all duration-200
      `}
    />
  </div>
);

// ============================================================================
// SECTION CARD COMPONENT
// ============================================================================

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center space-x-3 mb-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CompanyProfileSection = ({ onUpdate }) => {
  const { user, loading: authLoading, hasPermission } = useAuth();
  
  // State - exactly as original
  const [data, setData] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [startHour, setStartHour] = useState('');
  const [endHour, setEndHour] = useState('');
  const [dayPreset, setDayPreset] = useState('');
  // Reply hours — optional second tier. Blank = inherit initial (office) hours.
  const [replyStartHour, setReplyStartHour] = useState('');
  const [replyEndHour, setReplyEndHour] = useState('');
  const [replyDayPreset, setReplyDayPreset] = useState('');
  const [avgDealValueError, setAvgDealValueError] = useState('');

  // Permission check - exactly as original
  const canEditSettings = hasPermission(PERMISSIONS.VIEW_EDIT_COMPANY_INFO);

  // Load settings - exactly as original
  useEffect(() => {
    const loadSettings = async () => {
      if (authLoading) return;

      if (!user || !user.tenant_id) {
        setData({});
        setStartHour('');
        setEndHour('');
        setDayPreset('');
        setReplyStartHour('');
        setReplyEndHour('');
        setReplyDayPreset('');
        setSettingsLoading(false);
        return;
      }

      setSettingsLoading(true);
      setError('');
      
      try {
        const settings = await makeAuthenticatedRequest(`/settings?tenant_id=${user.tenant_id}`);

        // Fetch industry from tenants table
        const { data: tenant } = await supabase
          .from('tenants')
          .select('industry')
          .eq('id', user.tenant_id)
          .single();

        // Auto-populate industry if not already in settings
        if (tenant?.industry) {
          settings.industry = { value: tenant.industry };
        }

        setData(settings);

        const open = settings.officeOpenHour?.value || '';
        const close = settings.officeCloseHour?.value || '';
        const daysRaw = settings.officeDays?.value || '';
        const days = daysRaw ? daysRaw.split(',') : [];

        setStartHour(open);
        setEndHour(close);

        if (Array.isArray(days) && days.length > 0) {
          if (days.length === 7) setDayPreset('Everyday');
          else if (days.length === 6 && !days.includes('Sunday')) setDayPreset('M–Sat');
          else if (days.length === 5 && days.includes('Monday') && days.includes('Friday')) setDayPreset('M–F');
          else setDayPreset('');
        } else {
          setDayPreset('');
        }

        // Reply hours (optional; blank fields inherit initial hours downstream).
        const replyOpen = settings.replyOpenHour?.value || '';
        const replyClose = settings.replyCloseHour?.value || '';
        const replyDaysRaw = settings.replyDays?.value || '';
        const replyDaysArr = replyDaysRaw ? replyDaysRaw.split(',') : [];

        setReplyStartHour(replyOpen);
        setReplyEndHour(replyClose);

        if (Array.isArray(replyDaysArr) && replyDaysArr.length > 0) {
          if (replyDaysArr.length === 7) setReplyDayPreset('Everyday');
          else if (replyDaysArr.length === 6 && !replyDaysArr.includes('Sunday')) setReplyDayPreset('M–Sat');
          else if (replyDaysArr.length === 5 && replyDaysArr.includes('Monday') && replyDaysArr.includes('Friday')) setReplyDayPreset('M–F');
          else setReplyDayPreset('');
        } else {
          setReplyDayPreset('');
        }
      } catch (err) {
        console.error('Failed to load settings', err);
        setError(`Failed to load settings: ${err.message}`);
        setData({});
        setStartHour('');
        setEndHour('');
        setDayPreset('');
        setReplyStartHour('');
        setReplyEndHour('');
        setReplyDayPreset('');
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [user?.tenant_id, user?.role, authLoading]);

  // Clear messages - exactly as original
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Handle change - exactly as original
  const handleChange = (key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { id: key }),
        value,
      },
    }));
  };

  // Handle save - exactly as original
  const handleSave = async () => {
    if (!user || !user.tenant_id) {
      setError('Cannot save settings: User or tenant_id not available.');
      return;
    }
    
    if (!hasPermission(PERMISSIONS.VIEW_EDIT_COMPANY_INFO)) {
      setError("You don't have permission to save company settings.");
      return;
    }

    const dealErr = validateAvgDealValue(data.avg_deal_value?.value);
    if (dealErr) {
      setAvgDealValueError(dealErr);
      setError('Please fix the highlighted field before saving.');
      return;
    }
    setAvgDealValueError('');

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const dayMap = {
        'M–F': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        'M–Sat': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        'Everyday': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      };

      const translatedDays = dayPreset ? dayMap[dayPreset] || [] : [];
      // Reply days use the same full-name comma format. A blank preset stores ''
      // which loadTenantHours treats as absent, so that field falls back to the
      // initial (office) hours.
      const translatedReplyDays = replyDayPreset ? dayMap[replyDayPreset] || [] : [];

      const settingsPayload = {
        ...data,
        officeOpenHour: { value: startHour },
        officeCloseHour: { value: endHour },
        officeDays: { value: translatedDays.join(',') },
        replyOpenHour: { value: replyStartHour },
        replyCloseHour: { value: replyEndHour },
        replyDays: { value: translatedReplyDays.join(',') },
      };

      const cleanedSettings = {};
      Object.entries(settingsPayload).forEach(([key, entry]) => {
        // Industry is owned by onboarding (tenants.industry) and only mirrored
        // into `data` for display. Never persist it back into settings, or the
        // two copies can drift.
        if (key === 'industry') return;
        if (entry && typeof entry.value !== 'undefined') {
          cleanedSettings[key] = entry;
        }
      });

      if (Object.keys(cleanedSettings).length === 0) {
        setSaving(false);
        return;
      }
      
      await makeAuthenticatedRequest('/settings', {
        method: 'PUT',
        body: JSON.stringify({ 
          settings: cleanedSettings, 
          tenant_id: user.tenant_id 
        })
      });

      const newData = { ...settingsPayload };
      if (newData.officeDays) {
        newData.officeDays = { ...newData.officeDays, value: translatedDays.join(',') };
      }
      if (newData.replyDays) {
        newData.replyDays = { ...newData.replyDays, value: translatedReplyDays.join(',') };
      }
      setData(newData);

      setSuccess('Settings saved successfully!');
      onUpdate?.();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save failed', err);
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Calculate weekly hours for summary
  const getWeeklyHours = () => {
    if (!startHour || !endHour || !dayPreset) return null;
    const dailyHours = parseInt(endHour) - parseInt(startHour);
    const dayCount = dayPreset === 'Everyday' ? 7 : dayPreset === 'M–Sat' ? 6 : 5;
    return dailyHours * dayCount;
  };

  // Loading state
  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* Permission Warning */}
      {!canEditSettings && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700">
            You have read-only access. Admin permissions required to make changes.
          </span>
        </div>
      )}

      {/* Basic Company Information */}
      <SectionCard
        icon={Building2}
        iconColor="from-blue-500 to-blue-600"
        title="Basic Information"
        subtitle="Core business details and contact"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            icon={Building2}
            label="Company Name"
            value={data.company_name?.value || ''}
            onChange={(e) => handleChange('company_name', e.target.value)}
            placeholder="Your Company Name"
            disabled={!canEditSettings}
          />
          
          <FormSelect
            icon={Building2}
            label="Business Type"
            value={data.business_type?.value || ''}
            onChange={(e) => handleChange('business_type', e.target.value)}
            disabled={!canEditSettings}
            options={
              <>
                <option value="">Select type</option>
                {BUSINESS_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </>
            }
          />
          
          <FormInput
            icon={User}
            label="Primary Contact"
            value={data.primary_contact?.value || ''}
            onChange={(e) => handleChange('primary_contact', e.target.value)}
            placeholder="Contact Person"
            disabled={!canEditSettings}
          />
          
          <FormInput
            icon={Phone}
            label="Phone Number"
            value={data.phone?.value || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
            disabled={!canEditSettings}
          />
          
          <FormInput
            icon={Mail}
            label="Support Email"
            value={data.email?.value || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="support@yourcompany.com"
            disabled={!canEditSettings}
          />
          
          <FormSelect
            icon={Globe}
            label="Time Zone"
            value={data.timezone?.value || ''}
            onChange={(e) => handleChange('timezone', e.target.value)}
            disabled={!canEditSettings}
            options={
              <>
                <option value="">Select timezone</option>
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </>
            }
          />
        </div>
      </SectionCard>

      {/* Industry & Deal Value */}
      <SectionCard
        icon={Briefcase}
        iconColor="from-orange-500 to-orange-600"
        title="Industry & Deal Value"
        subtitle="Your business sector and typical deal size"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Industry is set during onboarding and is the single source of
              truth used across scoring, prompts and reporting. It is no longer
              manually editable here so the settings value can never diverge
              from what onboarding wrote to tenants.industry. */}
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <Briefcase className="w-4 h-4 text-slate-400" />
              <span>Industry</span>
            </label>
            <div className="relative">
              <div className="w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-xl text-slate-900 bg-slate-50 cursor-not-allowed">
                {data.industry?.value || 'Set during onboarding'}
              </div>
              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 pointer-events-none">
                <Lock className="w-4 h-4" />
              </span>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              Set during onboarding and used everywhere. Contact support to change it.
            </p>
          </div>

          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-slate-700 mb-2">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <span>
                Average Deal Value <span className="text-red-500">*</span>
              </span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500 pointer-events-none">$</span>
              <input
                type="number"
                inputMode="numeric"
                step="1"
                min={AVG_DEAL_VALUE_MIN}
                max={AVG_DEAL_VALUE_MAX}
                value={data.avg_deal_value?.value ?? ''}
                onChange={(e) => {
                  handleChange('avg_deal_value', e.target.value);
                  if (avgDealValueError) setAvgDealValueError('');
                }}
                onBlur={(e) => setAvgDealValueError(validateAvgDealValue(e.target.value))}
                placeholder="e.g., 7500"
                disabled={!canEditSettings}
                className={`
                  w-full pl-8 pr-4 py-2.5 border rounded-xl text-slate-900
                  placeholder-slate-400 bg-white
                  focus:outline-none focus:ring-2
                  disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
                  transition-all duration-200
                  ${avgDealValueError
                    ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                    : 'border-slate-200 focus:ring-orange-500/20 focus:border-orange-500'}
                `}
              />
            </div>
            {avgDealValueError ? (
              <p className="mt-1.5 text-xs text-red-600 flex items-center space-x-1">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{avgDealValueError}</span>
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-slate-500">
                Your typical revenue per closed deal — placement fee, wholesale fee, commission, etc. Used to estimate pipeline added when leads turn hot.
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Initial Message Hours */}
      <SectionCard
        icon={Clock}
        iconColor="from-emerald-500 to-emerald-600"
        title="Initial Message Hours"
        subtitle="When AI starts new outreach — cold-start messages and follow-ups"
      >
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <FormSelect
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                disabled={!canEditSettings}
                options={
                  <>
                    <option value="">Open</option>
                    {HOUR_OPTIONS.map(h => (
                      <option key={h.value} value={h.value}>{h.label}</option>
                    ))}
                  </>
                }
              />
              
              <span className="text-slate-400 font-medium">→</span>
              
              <FormSelect
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                disabled={!canEditSettings}
                options={
                  <>
                    <option value="">Close</option>
                    {CLOSING_HOUR_OPTIONS.map(h => (
                      <option key={h.value} value={h.value}>{h.label}</option>
                    ))}
                  </>
                }
              />
              
              <div className="hidden lg:block h-8 w-px bg-slate-300" />
              
              <FormSelect
                value={dayPreset}
                onChange={(e) => setDayPreset(e.target.value)}
                disabled={!canEditSettings}
                options={
                  <>
                    <option value="">Days</option>
                    {DAY_OPTIONS.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </>
                }
              />
            </div>
            
            {getWeeklyHours() && (
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{getWeeklyHours()}</span> hrs/week
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Reply Hours */}
      <SectionCard
        icon={Clock}
        iconColor="from-sky-500 to-sky-600"
        title="Reply Hours"
        subtitle="When AI can respond to leads who message back — usually a wider window"
      >
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex flex-wrap items-center gap-3">
            <FormSelect
              value={replyStartHour}
              onChange={(e) => setReplyStartHour(e.target.value)}
              disabled={!canEditSettings}
              options={
                <>
                  <option value="">Open</option>
                  {/* A previously-saved illegal hour (e.g. 6:00 AM) has no option any
                      more. Surface it as disabled rather than letting the select fall
                      blank, so the tenant can see what is still stored on their account. */}
                  {replyStartHour && !REPLY_OPEN_HOUR_OPTIONS.some(h => h.value === String(replyStartHour)) && (
                    <option value={replyStartHour} disabled>
                      {formatHour(replyStartHour)} (not allowed)
                    </option>
                  )}
                  {REPLY_OPEN_HOUR_OPTIONS.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </>
              }
            />

            <span className="text-slate-400 font-medium">→</span>

            <FormSelect
              value={replyEndHour}
              onChange={(e) => setReplyEndHour(e.target.value)}
              disabled={!canEditSettings}
              options={
                <>
                  <option value="">Close</option>
                  {replyEndHour && !REPLY_CLOSING_HOUR_OPTIONS.some(h => h.value === String(replyEndHour)) && (
                    <option value={replyEndHour} disabled>
                      {formatHour(replyEndHour)} (not allowed)
                    </option>
                  )}
                  {REPLY_CLOSING_HOUR_OPTIONS.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </>
              }
            />

            <div className="hidden lg:block h-8 w-px bg-slate-300" />

            <FormSelect
              value={replyDayPreset}
              onChange={(e) => setReplyDayPreset(e.target.value)}
              disabled={!canEditSettings}
              options={
                <>
                  <option value="">Days</option>
                  {DAY_OPTIONS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </>
              }
            />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Any field left blank inherits your Initial Message Hours. Reply hours only affect responses to leads who text back, never new outreach.
          </p>

          {/* Education — always shown, so the limit is understood before it's hit. */}
          <div className="mt-3 flex gap-2.5 rounded-lg border border-sky-200 bg-sky-50 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div className="text-xs leading-relaxed text-sky-900">
              <span className="font-semibold">Why you can't pick earlier or later.</span>{' '}
              Federal law (TCPA) only allows business texts between {TCPA_WINDOW_LABEL}. That
              applies to replies too, not just first outreach. Texting outside that window
              carries penalties per message, so SurFox holds anything that falls outside it and
              sends at the next legal opening instead. Nothing is lost, it just waits.
            </div>
          </div>

          {/* Correction notice — only for accounts still holding an out-of-bounds value. */}
          {isOutOfTcpaBounds(replyStartHour, replyEndHour) && (() => {
            const eff = clampToTcpaHours(replyStartHour, replyEndHour);
            return (
              <div className="mt-3 flex gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="text-xs leading-relaxed text-amber-900">
                  <span className="font-semibold">We adjusted your reply window.</span>{' '}
                  Your account is set to {formatHour(replyStartHour)} – {formatHour(replyEndHour)},
                  which falls outside the legal {TCPA_WINDOW_LABEL} window. SurFox is currently
                  sending {formatHour(eff.openHour)} – {formatHour(eff.closeHour)} instead, and has
                  been since this safeguard went live. No messages were lost. Pick an allowed time
                  above and save to clear this notice.
                </div>
              </div>
            );
          })()}
        </div>
      </SectionCard>

      {/* Location & Service Areas */}
      <SectionCard
        icon={MapPin}
        iconColor="from-violet-500 to-violet-600"
        title="Location & Service Areas"
        subtitle="Where you operate"
      >
        <div className="space-y-4">
          <FormTextarea
            icon={MapPin}
            label="Business Address"
            value={data.address?.value || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="123 Main Street, City, State, ZIP"
            rows={2}
            disabled={!canEditSettings}
          />
          
          <FormTextarea
            icon={Globe}
            label="Service Regions"
            value={data.regions?.value || ''}
            onChange={(e) => handleChange('regions', e.target.value)}
            placeholder="Cities, counties, or regions you serve"
            rows={2}
            disabled={!canEditSettings}
          />
        </div>
      </SectionCard>

      {/* Configuration Summary */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-slate-200">
            <Building2 className="w-4 h-4 text-slate-600" />
          </div>
          <span className="font-medium text-slate-900">Current Configuration</span>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Company</div>
            <div className="font-medium text-slate-900 truncate">
              {data.company_name?.value || 'Not set'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Industry</div>
            <div className="font-medium text-slate-900 truncate">
              {data.industry?.value || 'Not set'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Hours</div>
            <div className="font-medium text-slate-900">
              {startHour && endHour 
                ? `${startHour}:00 – ${endHour}:00` 
                : 'Not set'}
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Days</div>
            <div className="font-medium text-slate-900">
              {dayPreset || 'Not set'}
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          These details personalize AI conversations and ensure messages are sent during business hours.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-end pt-2">
        <motion.button
          onClick={handleSave}
          disabled={!canEditSettings || saving}
          whileTap={{ scale: 0.98 }}
          className={`
            px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
            flex items-center space-x-2
            ${success 
              ? 'bg-emerald-500 text-white' 
              : 'bg-slate-900 text-white hover:bg-slate-800'
            }
            ${(!canEditSettings || saving) ? 'opacity-50 cursor-not-allowed' : 'shadow-sm hover:shadow'}
          `}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : success ? (
            <>
              <Check className="w-4 h-4" />
              <span>Saved</span>
            </>
          ) : (
            <span>Save Changes</span>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default CompanyProfileSection;